import {useResilientRestQuery} from '@famly/stat_apollo-query';
import {toRecord} from 'web-app/util/array';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type {Availability, Session, GetSessionsResponse, GetSessionsVariables} from '../helpers/session-types.js';
import {GetSessions} from '../queries/get-sessions.rest';
import {type SessionIndex, type TimeRange} from '../helpers/patch-missing-session-booking-times.js';

type Result = {sessions: SessionIndex | null; loading: boolean};

/**
 * Requests sessions for the site from the `sessions` REST endpoint and
 * indexes the sessions for availability.
 *
 * @param siteId The site to get the sessions for.
 * @returns The indexed sessions and a loading flag.
 */
export const useGetSessionAvailability = (siteId: string): Result => {
    // Consider using useRestLazyQuery to allow conditionally loading sessions.
    const {data, loading} = useResilientRestQuery<GetSessionsResponse, GetSessionsVariables>(GetSessions, {
        variables: {
            institutionId: siteId,
            includeHiddenByUser: true,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-and-network',
    });
    const indexAvailabilities = (availabilities: Availability[]): {[day: string]: TimeRange} =>
        toRecord(
            availabilities,
            availability => availability.day,
            availability => availability, // Using the magic of structural sub-typing.
        );
    const indexSessions = (sessions: Session[]): SessionIndex =>
        toRecord(
            sessions,
            session => session.id,
            ({flexibleHours, availability}: Session) => ({
                flexibleHours,
                availability: indexAvailabilities(availability),
            }),
        );

    return {
        sessions: hasValue(data) ? indexSessions(data.result.sessions) : null,
        loading,
    };
};
